const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const trimmed = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  return text.length > 0 ? text : undefined;
};

/** First message out of a ModelState / ValidationProblemDetails `errors` bag. */
const firstValidationMessage = (errors: unknown): string | undefined => {
  if (!isRecord(errors)) return undefined;

  for (const value of Object.values(errors)) {
    const direct = trimmed(value);
    if (direct) return direct;

    if (Array.isArray(value)) {
      const first = value.map(trimmed).find(Boolean);
      if (first) return first;
    }
  }

  return undefined;
};

/**
 * Pulls the message the AIKA backend sent along with a failed request.
 *
 * `src/api/util.ts` rejects with the raw `AxiosResponse`, so errors reaching a
 * mutation's `onError` look like `{ status, data }`, where `data` is either the
 * `ErrorDetails` shape written by `ExceptionMiddleware` (`{ StatusCode, Message }`),
 * a `ValidationProblemDetails` from `BadRequest(ModelState)`, or a plain string.
 *
 * Only client errors are surfaced: `ExceptionMiddleware` puts
 * `InnerException.ToString()` — a full stack trace — in the message for 5xx.
 */
export const getBackendErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (!isRecord(error)) return fallback;

  const { status, data } = error;
  if (typeof status !== "number" || status < 400 || status >= 500) {
    return fallback;
  }

  const asString = trimmed(data);
  if (asString) return asString;

  if (!isRecord(data)) return fallback;

  return (
    trimmed(data.Message) ??
    trimmed(data.message) ??
    firstValidationMessage(data.errors) ??
    trimmed(data.title) ??
    fallback
  );
};
